import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable } from 'rxjs';
import { AbcComponentService } from '../abcComponent.service';
import { IMenu, MenuDataSource, OrientationMode, SubmenuDirectionMode } from './abcMenu';
import { DevExMenuComponent } from '../../../devExtreme/src/DevExMenu/devExMenu.component';
import { ComponentStyle } from '../abcComponent.model';

@Component({
    selector: 'abcMenu',
    template: '<ng-template #abcMenu></ng-template>',
    standalone: false
})
export class AbcMenuComponent implements OnInit {
  @ViewChild('abcMenu', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  @Input('dataSource') dataSource: MenuDataSource;
  @Input('displayExpr') displayExpr: string;
  @Output() onItemClick = new EventEmitter();
  @Input() style: ComponentStyle = ComponentStyle.DevExpress;
  private _width: string;
  private _height: string;
  private _class: string;
  private _showFirstSubmenuMode: string;
  private _orientation: OrientationMode;
  private _submenuDirection: SubmenuDirectionMode;
  private _hideSubmenuOnMouseLeave: boolean;
  private _hoverStateEnabled: boolean;
  private _rtlEnabled: boolean;
  private _disabled: boolean;
  private _visible: boolean;
  private _activeStateEnabled: boolean;
  private _adaptivityEnabled: boolean;
  private _focusStateEnabled: boolean;
  private _selectByClick: boolean;

  @Input()
  get width() {
    return this._width;
  }
  set width(data) {
    if (this.componentRef) {
      this.componentRef.width = data;
    }
    this._width = data;
  }

  @Input()
  get height() {
    return this._height;
  }
  set height(data) {
    if (this.componentRef) {
      this.componentRef.height = data;
    }
    this._height = data;
  }

  @Input()
  get visible() {
    return this._visible;
  }
  set visible(data) {
    if (this.componentRef) {
      this.componentRef.visible = data;
    }
    this._visible = data;
  }

  @Input()
  get class() {
    return this._class;
  }
  set class(data) {
    if (this.componentRef) {
      this.componentRef.class = data;
    }
    this._class = data;
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(data) {
    if (this.componentRef) {
      this.componentRef.disabled = data;
    }
    this._disabled = data;
  }

  @Input()
  get activeStateEnabled() {
    return this._activeStateEnabled;
  }
  set activeStateEnabled(data) {
    if (this.componentRef) {
      this.componentRef.activeStateEnabled = data;
    }
    this._activeStateEnabled = data;
  }

  @Input()
  get selectByClick() {
    return this._selectByClick;
  }
  set selectByClick(data) {
    if (this.componentRef) {
      this.componentRef.selectByClick = data;
    }
    this._selectByClick = data;
  }

  @Input()
  get focusStateEnabled() {
    return this._focusStateEnabled;
  }
  set focusStateEnabled(data) {
    if (this.componentRef) {
      this.componentRef.focusStateEnabled = data;
    }
    this._focusStateEnabled = data;
  }

  @Input()
  get adaptivityEnabled() {
    return this._adaptivityEnabled;
  }
  set adaptivityEnabled(data) {
    if (this.componentRef) {
      this.componentRef.adaptivityEnabled = data;
    }
    this._adaptivityEnabled = data;
  }

  @Input()
  get submenuDirection() {
    return this._submenuDirection;
  }
  set submenuDirection(data) {
    if (this.componentRef) {
      this.componentRef.submenuDirection = data;
    }
    this._submenuDirection = data;
  }

  @Input()
  get hoverStateEnabled() {
    return this._hoverStateEnabled;
  }
  set hoverStateEnabled(data) {
    if (this.componentRef) {
      this.componentRef.hoverStateEnabled = data;
    }
    this._hoverStateEnabled = data;
  }

  @Input()
  get rtlEnabled() {
    return this._rtlEnabled;
  }
  set rtlEnabled(data) {
    if (this.componentRef) {
      this.componentRef.rtlEnabled = data;
    }
    this._rtlEnabled = data;
  }

  @Input()
  get hideSubmenuOnMouseLeave() {
    return this._hideSubmenuOnMouseLeave;
  }
  set hideSubmenuOnMouseLeave(data) {
    if (this.componentRef) {
      this.componentRef.hideSubmenuOnMouseLeave = data;
    }
    this._hideSubmenuOnMouseLeave = data;
  }

  @Input()
  get showFirstSubmenuMode() {
    return this._showFirstSubmenuMode;
  }
  set showFirstSubmenuMode(data) {
    if (this.componentRef) {
      this.componentRef.showFirstSubmenuMode = data;
    }
    this._showFirstSubmenuMode = data;
  }

  @Input()
  get orientation() {
    return this._orientation;
  }
  set orientation(data) {
    if (this.componentRef) {
      this.componentRef.orientation = data;
    }
    this._orientation = data;
  }

  private componentRef;
  private eventListener = [];
  public menuAttributes: IMenu;
  constructor(private abcComponentService: AbcComponentService) {}

  ngOnInit() {
    this.setMenuAttributesIfAvailable();
    this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      this.componentRef = this.abcComponentService.loadComponent(
        DevExMenuComponent,
        this.viewContainerRef
      ).componentRef.instance;
      this.componentRef.dataSource = this.menuAttributes.dataSource;
      this.componentRef.displayExpr = this.menuAttributes.displayExpr;
      this.componentRef.showFirstSubmenuMode = this.menuAttributes.showFirstSubmenuMode;
      this.componentRef.orientation = this.menuAttributes.orientation ? this.menuAttributes.orientation : 'horizontal';
      this.componentRef.submenuDirection = this.menuAttributes.submenuDirection
        ? this.menuAttributes.submenuDirection
        : 'auto';
      this.componentRef.width = this.menuAttributes.width;
      this.componentRef.height = this.menuAttributes.height;
      this.componentRef.hoverStateEnabled = this.menuAttributes.hoverStateEnabled;
      this.componentRef.rtlEnabled = this.menuAttributes.rtlEnabled;
      this.componentRef.visible = this.menuAttributes.visible;
      this.componentRef.activeStateEnabled = this.menuAttributes.activeStateEnabled;
      this.componentRef.adaptivityEnabled = this.menuAttributes.adaptivityEnabled;
      this.componentRef.focusStateEnabled = this.menuAttributes.focusStateEnabled;
      this.componentRef.class = this.menuAttributes.class;
      this.componentRef.selectByClick = this.menuAttributes.selectByClick;
      this.componentRef.onItemClick.subscribe((event) => {
        this.itemClick(event);
      });
      observer.next(this.componentRef);
    });
    return observable;
  }

  private setMenuAttributesIfAvailable(): void {
    this.menuAttributes = {
      width: this.width,
      height: this.height,
      dataSource: this.dataSource,
      displayExpr: this.displayExpr,
      showFirstSubmenuMode: this.showFirstSubmenuMode,
      orientation: this.orientation,
      submenuDirection: this.submenuDirection,
      hideSubmenuOnMouseLeave: (this.hideSubmenuOnMouseLeave = false),
      visible: (this.visible = true),
      disabled: this.disabled,
      hoverStateEnabled: this.hoverStateEnabled,
      rtlEnabled: this.rtlEnabled,
      activeStateEnabled: this.activeStateEnabled,
      adaptivityEnabled: this.adaptivityEnabled,
      focusStateEnabled: this.focusStateEnabled,
      selectByClick: this.selectByClick,
      class: this.class,
    };
  }

  private itemClick(newValue) {
    this.onItemClick.emit({ newValue: newValue });
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    this.eventListener.push({ type, listener });
  }

  public refresh(): void {
    this.componentRef.refresh();
  }
}
